import { formatPriceBookCellValue } from '@/document-render/domain/formatDocumentMoneyCell';
import type { PriceBookSubsectionKey } from './priceBookRowModel';

export type PriceBookMoneyKey = 'regular' | 'ot1' | 'ot2';

export type PriceBookSheetColumnKey = 'name' | 'unit' | PriceBookMoneyKey;

/** Fixed subsection order; empty subsections are omitted (design ruling 2026-09-15). */
export const PRICE_BOOK_SUBSECTIONS: ReadonlyArray<{ key: PriceBookSubsectionKey; label: string }> = [
  { key: 'equipment', label: 'Equipment rates' },
  { key: 'labor', label: 'Labor rates' },
  { key: 'material', label: 'Material rates' },
  { key: 'additional', label: 'Additional rates' }
];

/**
 * Usage tiers and overtime are two axes, not one (design ruling 2026-09-17): equipment `ot1`/`ot2`
 * are the sheet's T2/T3 usage-tier rates, labor `ot1`/`ot2` are overtime and double time. The two
 * meanings never share a label.
 */
const MONEY_LABELS: Readonly<Record<PriceBookSubsectionKey, Readonly<Record<PriceBookMoneyKey, string>>>> = {
  equipment: { regular: 'Regular', ot1: 'T2 Rate', ot2: 'T3 Rate' },
  labor: { regular: 'Regular', ot1: 'Overtime', ot2: 'Double Time' },
  material: { regular: 'Regular', ot1: 'OT 1', ot2: 'OT 2' },
  additional: { regular: 'Regular', ot1: 'OT 1', ot2: 'OT 2' }
};

export function priceBookMoneyLabel(subsection: PriceBookSubsectionKey, key: PriceBookMoneyKey): string {
  return MONEY_LABELS[subsection][key];
}

export function priceBookSheetColumnLabel(subsection: PriceBookSubsectionKey, key: PriceBookSheetColumnKey): string {
  if (key === 'name') return 'Rate';
  if (key === 'unit') return 'Unit';
  return priceBookMoneyLabel(subsection, key);
}

export const PRICE_BOOK_EMPTY_MONEY = '—';

/** A missing rate is a dash, never `$0.00`; a present one is formatted as a `rate` cell. */
export function formatPriceBookMoney(value: number | null, currency: string, cultureTag: string): string {
  if (value === null) return PRICE_BOOK_EMPTY_MONEY;
  return formatPriceBookCellValue(value, 'rate', currency, cultureTag);
}
